// src/pages/EventPage.jsx – обновленная версия с WorkersSelector
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import Modal from "react-modal";
import Spinner from "../components/Spinner";
import EventLayout from "../components/EventLayout";
import WorkersSelector from "../components/WorkersSelector";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  PencilSquareIcon,
  CalendarIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import getSymbolFromCurrency from "currency-symbol-map";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../context/AuthContext";
import { SERVER_BASE_URL } from "../config";
import PropTypes from "prop-types";
import { getEventStatus, getStatusColorClass } from "../utils/api";
import {
  toInputLocal,
  fromInputLocalToUtcIso,
  formatLocal
} from "../utils/dateUtils";

Modal.setAppElement("#root");

/* =============================================================================
   EventPage – детальная версия с множественным выбором работников
   =============================================================================*/
function EventPage() {
  /* ------------------------ context & params ------------------------ */
  const { id } = useParams();
  const { user } = useAuth();

  /* ----------------------------- state ------------------------------ */
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  /* flyer */
  const [flyerPreview, setFlyerPreview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  /* editables */
  const [placeValue, setPlaceValue] = useState("");
  const [admissionValue, setAdmissionValue] = useState("");
  const [startTimeValue, setStartTimeValue] = useState("");
  const [durationValue, setDurationValue] = useState("8");

  const [isEditingPlace, setIsEditingPlace] = useState(false);
  const [isEditingAdmission, setIsEditingAdmission] = useState(false);
  const [isEditingStartTime, setIsEditingStartTime] = useState(false);
  const [isEditingDuration, setIsEditingDuration] = useState(false);

  /* description */
  const [desc, setDesc] = useState("");
  const [descDirty, setDescDirty] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);

  const getStatusColor = () => {
    if (!event) return "bg-gray-100 text-gray-800";
    const status = getEventStatus(event);
    return getStatusColorClass(status);
  };

  // В самом начале файла
const EntranceWorkersIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);


  /* ----------------------------- API utils -------------------------- */
  const authFetch = async (endpoint, options = {}) => {
    const token = localStorage.getItem("token");
    return fetch(`${SERVER_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  };

  /* fetch helpers */
  const fetchEvent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/events/${id}`);
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setEvent(data);
      setAdmissionValue(data.admission !== null && data.admission !== undefined ? String(data.admission) : "");
      setDesc(data.description || "");

      if (data.flyer_url) setFlyerPreview(data.flyer_url);
      if (data.event_duration !== null && data.event_duration !== undefined) {
        setDurationValue(String(data.event_duration));
      } else if (data.start_time && data.end_time) {
        const start = new Date(data.start_time);
        const end = new Date(data.end_time);
        setDurationValue(((end - start) / 36e5).toFixed(1));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  /* ---------------------------- effects ----------------------------- */
  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  /* ------------------------- description save ----------------------- */
  const saveDescription = async () => {
    if (!descDirty || savingDesc) return;
    setSavingDesc(true);
    try {
      const res = await authFetch(`/api/events/${event.id}`, {
        method: "PUT",
        body: JSON.stringify({ ...event, description: desc }),
      });
      if (res.ok) {
        setEvent(await res.json());
        setDescDirty(false);
        toast.success("Description saved!");
      } else toast.error("Failed to save description");
    } catch (err) {
      toast.error("Error while saving");
    } finally {
      setSavingDesc(false);
    }
  };

  /* --------------------------- flyer upload ------------------------- */
  const handleFileSelect = () => {
    // Проверяем, что ref существует перед вызовом click
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error("File input reference is not available");
      toast.error("Could not access file selector");
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif", "image/heic", "image/heif"];
    
    if (!allowed.includes(file.type)) {
      toast.error("Invalid file format. Please upload a valid image file.");
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("flyer", file);
      
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("You must be logged in to upload files");
        return;
      }
      
      toast.info("Uploading flyer...", { autoClose: 2000 });
      
      const res = await fetch(`${SERVER_BASE_URL}/api/events/${id}/flyer/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Upload failed");
      }
      
      const data = await res.json();
      setFlyerPreview(data.flyer_url);
      toast.success("Flyer uploaded successfully");
    } catch (err) {
      console.error("Error uploading file:", err);
      toast.error(err.message || "Upload failed. Please try again.");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileChange({ target: { files } });
    }
  };

  /* ------------------------------ render ---------------------------- */
  if (loading) {
    return (
      <div className="container mx-auto p-4 mt-16 flex justify-center items-center" style={{ minHeight: "60vh" }}>
        <Spinner />
      </div>
    );
  }
  if (!event) {
    return (
      <div className="container mx-auto p-4 mt-16">
        <p>Event not found.</p>
      </div>
    );
  }

  const currency = event.currency || user?.default_currency || "USD";

  return (
    <EventLayout event={event} hideDescription={true}>
      <div className="container mx-auto">
        {/* --------------------------- GRID ---------------------------- */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* --------------------------- Flyer --------------------------- */}
          <div className="lg:w-1/2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              {flyerPreview ? (
                <div className="flex flex-col items-center w-full">
                  <div
                    className="relative overflow-hidden rounded-lg shadow-sm cursor-pointer transition-transform hover:shadow-md hover:scale-[1.01]"
                    style={{ width: "100%" }}
                    onClick={() => setIsModalOpen(true)}
                  >
                    <img
                      src={flyerPreview}
                      alt="Flyer Preview"
                      className="w-full object-cover"
                      style={{ maxHeight: "600px" }}
                    />
                  </div>
                  {/* Вернули центрирование кнопок */}
                  <div className="mt-4 flex justify-center space-x-4">
                    <button
                      onClick={handleFileSelect}
                      className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      <ArrowUpTrayIcon className="w-4 h-4" />
                      <span>Upload</span>
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(flyerPreview);
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = "flyer.jpg";
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error(err);
                          toast.error("Failed to download flyer");
                        }
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </div>
                  
                  {/* Modal with flyer */}
                  <Modal
                    isOpen={isModalOpen}
                    onRequestClose={() => setIsModalOpen(false)}
                    overlayClassName="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000]"
                    className="outline-none"
                  >
                    <div className="relative">
                      <img
                        src={flyerPreview}
                        alt="Full Flyer"
                        className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-xl"
                      />
                      <button
                        onClick={() => setIsModalOpen(false)}
                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-black/60 text-white rounded-full hover:bg-black/80"
                      >
                        ×
                      </button>
                    </div>
                  </Modal>
                </div>
              ) : (
                <div
                  onClick={handleFileSelect}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed ${
                    isDragging ? "border-indigo-500 bg-indigo-50" : "border-gray-300 bg-gray-50"
                  } rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-300 transition-colors duration-200`}
                  style={{ minHeight: "300px" }}
                >
                  <ArrowUpTrayIcon className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-gray-500 text-center">
                    {isDragging ? "Drop to upload" : "Drag & drop image, or click to select"}
                  </p>
                </div>
              )}
              {/* Скрытый input для загрузки файла */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/heic,image/heif"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>

          {/* -------------------------- Details -------------------------- */}
          <div className="lg:w-1/2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Event Details</h2>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
                  {new Date() < new Date(event.start_time)
                    ? "Upcoming"
                    : new Date() >= new Date(event.start_time) && event.end_time && new Date() <= new Date(event.end_time)
                    ? "Ongoing"
                    : "Past"}
                </span>
              </div>
              {/* split rows */}
              <FieldRow
                icon={MapPinIcon}
                label="Place"
                editing={isEditingPlace}
                display={event.place || "—"}
                inputNode={
                  <input
                    className="border-b border-indigo-500 bg-indigo-50 px-2 py-1 focus:outline-none rounded w-1/2 text-right"
                    value={placeValue}
                    onChange={(e) => setPlaceValue(e.target.value)}
                    onBlur={async () => {
                      setIsEditingPlace(false);
                      if (placeValue === event.place) return;
                      try {
                        const res = await authFetch(`/api/events/${event.id}`, {
                          method: "PUT",
                          body: JSON.stringify({ ...event, place: placeValue }),
                        });
                        if (res.ok) {
                          setEvent(await res.json());
                          toast.success("Place updated");
                        } else toast.error("Failed to update place");
                      } catch (err) {
                        toast.error("Error");
                      }
                    }}
                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                    autoFocus
                  />
                }
                onDisplayClick={() => {
                  setPlaceValue(event.place || "");
                  setIsEditingPlace(true);
                }}
              />

              <FieldRow
                icon={CurrencyDollarIcon}
                label="Admission"
                editing={isEditingAdmission}
                display={event.admission ? `${event.admission} ${getSymbolFromCurrency(currency)}` : "—"}
                inputNode={
                  <span className="flex items-center">
                    <input
                      className="border-b border-indigo-500 bg-indigo-50 px-2 py-1 focus:outline-none rounded w-24 text-right"
                      value={admissionValue}
                      onChange={(e) => setAdmissionValue(e.target.value)}
                      onBlur={async () => {
                        setIsEditingAdmission(false);
                        if (admissionValue === event.admission) return;
                        try {
                          const res = await authFetch(`/api/events/${event.id}`, {
                            method: "PUT",
                            body: JSON.stringify({ ...event, admission: admissionValue }),
                          });
                          if (res.ok) {
                            setEvent(await res.json());
                            toast.success("Admission updated");
                          } else toast.error("Failed to update admission");
                        } catch (err) {
                          toast.error("Error");
                        }
                      }}
                      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                      autoFocus
                    />
                    <span className="ml-1">{getSymbolFromCurrency(currency)}</span>
                  </span>
                }
                onDisplayClick={() => {
                  setAdmissionValue(event.admission || "");
                  setIsEditingAdmission(true);
                }}
              />

              <FieldRow
                icon={CalendarIcon}
                label="Event Start"
                editing={isEditingStartTime}
                display={formatLocal(event.start_time)}
                inputNode={
                  <input
                    type="datetime-local"
                    className="border-b border-indigo-500 bg-indigo-50 px-2 py-1 focus:outline-none rounded"
                    value={startTimeValue}
                    onChange={e => setStartTimeValue(e.target.value)}
                    onBlur={async () => {
                      setIsEditingStartTime(false);
                      if (!startTimeValue) return;
                      try {
                        const iso = fromInputLocalToUtcIso(startTimeValue);
                        const res = await authFetch(`/api/events/${event.id}`, {
                          method: "PUT",
                          body: JSON.stringify({ ...event, start_time: iso }),
                        });
                        if (res.ok) {
                          setEvent(await res.json());
                          toast.success("Start time updated");
                        } else {
                          toast.error("Failed to update start time");
                        }
                      } catch (err) {
                        console.error(err);
                        toast.error("Error");
                      }
                    }}
                    onKeyDown={e => e.key === "Enter" && e.target.blur()}
                    autoFocus
                  />
                }
                onDisplayClick={() => {
                  setStartTimeValue(toInputLocal(event.start_time));
                  setIsEditingStartTime(true);
                }}
              />

              <FieldRow
                icon={ClockIcon}
                label="Duration (hours)"
                editing={isEditingDuration}
                display={durationValue ? `${durationValue} hours` : "—"}
                inputNode={
                  <input
                    type="number"
                    className="border-b border-indigo-500 bg-indigo-50 px-2 py-1 focus:outline-none rounded w-20 text-right"
                    value={durationValue}
                    onChange={(e) => setDurationValue(e.target.value)}
                    onBlur={async () => {
                      setIsEditingDuration(false);
                      if (isNaN(parseFloat(durationValue))) return;
                      if (!event.start_time) {
                        toast.error("Set start time first");
                        return;
                      }
                      
                      try {
                        // Convert the string value to a number
                        const durationHours = parseFloat(durationValue);
                        
                        const res = await authFetch(`/api/events/${event.id}`, {
                          method: "PUT",
                          body: JSON.stringify({ 
                            event_duration: durationHours,
                            start_time: event.start_time // Include the existing start_time to prevent NULL values
                          }),
                        });
                        
                        if (res.ok) {
                          const updatedEvent = await res.json();
                          setEvent({
                            ...updatedEvent,
                            event_duration: durationHours
                          });
                          toast.success("Duration updated");
                        } else toast.error("Failed to update duration");
                      } catch (err) {
                        console.error("Error updating duration:", err);
                        toast.error("Error");
                      }
                    }}
                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                    autoFocus
                  />
                }
                onDisplayClick={() => setIsEditingDuration(true)}
              />

              {/* Заменяем старое поле Entrance на новый компонент WorkersSelector */}
              {user?.role === "organizer" && (
  <div className="py-3 flex justify-between items-center">
  <div className="flex items-center text-gray-600">
    <EntranceWorkersIcon className="w-5 h-5 mr-2" />
    <span>Entrance Workers</span>
  </div>
  <div className="flex items-center min-h-[38px]">
    <WorkersSelector eventId={event.id} organizerId={user.id} />
  </div>
</div>
)}
            </div>
          </div>
        </div>

        {/* ------------------------- Description ------------------------ */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6 relative">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Description</h2>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Click to add description"
            value={desc}
            onChange={(e) => { setDesc(e.target.value); setDescDirty(true); }}
            style={{ minHeight: "200px" }}
          />
          {descDirty && (
            <button
              onClick={saveDescription}
              disabled={savingDesc}
              className="mt-2 ml-auto bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded shadow disabled:opacity-40"
            >
              {savingDesc ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>
    </EventLayout>
  );
}

/* ---------------------- FieldRow reusable ---------------------- */
function FieldRow({ icon: Icon, label, editing, display, inputNode, onDisplayClick }) {
  return (
    <div className="py-3 flex justify-between items-center">
      <div className="flex items-center text-gray-600">
        <Icon className="w-5 h-5 mr-2" />
        <span>{label}</span>
      </div>
      {editing ? (
        inputNode
      ) : (
        <div
          onClick={onDisplayClick}
          className="group flex items-center cursor-pointer text-gray-900 hover:text-indigo-600 px-2 py-1 rounded hover:bg-gray-50"
        >
          <span className="mr-1 font-medium max-w-[220px] truncate">{display}</span>
          <PencilSquareIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
        </div>
      )}
    </div>
  );
}

FieldRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  editing: PropTypes.bool.isRequired,
  display: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  inputNode: PropTypes.node.isRequired,
  onDisplayClick: PropTypes.func.isRequired,
};

export default EventPage;